/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useSpring, animated } from '@react-spring/web';
import { IconButton, IconComponentType } from '../icon-button';
import { cn } from '@restworld/utils-common';

interface IAccordionV2Props {
  children: React.ReactNode;
  open?: boolean;
  className?: string;
}

interface AccordionContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AccordionContext = React.createContext<AccordionContextType>({
  isOpen: false,
  setIsOpen: () => {},
});

export const useAccordionContext = () => React.useContext(AccordionContext);

export const AccordionV2 = ({ children, open, className, ...rest }: IAccordionV2Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  React.useEffect(() => {
    if (typeof open !== 'undefined') setIsOpen(open);
  }, [open]);
  return (
    <AccordionContext.Provider value={{ isOpen, setIsOpen }}>
      <Wrapper className={className} {...rest}>
        {children}
      </Wrapper>
    </AccordionContext.Provider>
  );
};
export default AccordionV2;

const Wrapper = ({ children, open, className, ...rest }: IAccordionV2Props) => {
  return (
    <Disclosure as="div" className={className} {...rest}>
      {children}
    </Disclosure>
  );
};

/**
 * Last-resort name for the toggle in the `IconComponent` variant.
 *
 * In that variant the icon button is the only focusable control, so an empty
 * label left it unnamed for screen readers. Callers should pass
 * `iconAriaLabel` with the title of the section the toggle belongs to, which
 * is what `SectionAccordion` does; this generic wording only covers the
 * callers that pass nothing.
 */
export const ACCORDION_TOGGLE_FALLBACK_LABEL = 'Mostra o nascondi il contenuto';

const AccordionButton = ({
  children,
  onClick,
  icon,
  wrapperStyle,
  IconComponent,
  iconAriaLabel,
  className,
  ...rest
}: IAccordionButton) => {
  const { isOpen, setIsOpen } = useAccordionContext();

  if (IconComponent) {
    const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
      onClick?.(e);
      setIsOpen(!isOpen);
    };
    return (
      // The row stays a plain div: it is a mouse convenience on top of the
      // real control. Keyboard and screen-reader users operate the icon
      // button below, which carries the name and the expanded state. Making
      // the row itself a button would nest a button inside a button.
      <ClickableRow onClick={handleToggle} {...rest}>
        <ButtonContentWrapper twStyle={wrapperStyle} className={className}>
          {children}
          <StyledIconContainer isOpen={isOpen}>
            <IconButton
              ariaLabel={iconAriaLabel || ACCORDION_TOGGLE_FALLBACK_LABEL}
              aria-expanded={isOpen}
              Icon={IconComponent}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                handleToggle(e);
              }}
            />
          </StyledIconContainer>
        </ButtonContentWrapper>
      </ClickableRow>
    );
  }

  return (
    <Disclosure.Button
      className={className}
      onClick={(e) => {
        onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
        setIsOpen(!isOpen);
      }}
      {...rest}
    >
      <ButtonContentWrapper twStyle={wrapperStyle}>
        {children}
        <StyledIconContainer isOpen={isOpen}>
          {icon ? icon : <ChevronDownIcon className="h-4 w-4" />}
        </StyledIconContainer>
      </ButtonContentWrapper>
    </Disclosure.Button>
  );
};
const AccordionPanel = ({ children, ...rest }: IAccordionPanelProps) => {
  const { isOpen } = useAccordionContext();
  // add accordion animation from top to down using react-spring
  const accordionAnimation = useSpring({
    height: isOpen ? 'auto' : 0,
    opacity: isOpen ? 1 : 0,
    marginTop: isOpen ? 0 : -3.5,
    config: { tension: 300, friction: 20 },
  });

  return (
    <animated.div style={{ ...accordionAnimation }}>
      <Transition show={isOpen}>
        <Disclosure.Panel {...rest}>{children}</Disclosure.Panel>
      </Transition>
    </animated.div>
  );
};

interface IAccordionButton extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  wrapperStyle?: string;
  IconComponent?: IconComponentType;
  /**
   * Accessible name of the toggle in the `IconComponent` variant, ignored
   * otherwise (the default variant is a real button and takes its name from
   * `children`). Pass the title of the section being toggled, so the control
   * is distinguishable when several accordions sit on the same page.
   */
  iconAriaLabel?: string;
}
interface IAccordionPanelProps {
  children?: React.ReactNode;
  className?: string;
}

const ClickableRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('cursor-pointer w-full', className)} {...props} />
);
const StyledIconContainer = ({
  isOpen,
  className,
  ...props
}: { isOpen: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('h-fit w-fit transform transition-transform duration-150', isOpen && 'rotate-180', className)}
    {...props}
  />
);
const ButtonContentWrapper = ({
  twStyle,
  className,
  ...props
}: { twStyle?: string } & React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-between items-center', twStyle, className)} {...props} />
);

AccordionV2.Button = AccordionButton;
AccordionV2.Panel = AccordionPanel;
